/**
 * Import Survey component
 * Allows users to upload document files (TXT, PDF) to create surveys using AI.
 * Handles file validation, upload progress, error handling, and automatic retries.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Card, Alert, Spinner, ProgressBar } from 'react-bootstrap';
import { importSurvey } from '../services/api';

const ImportSurvey = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const progressIntervalRef = useRef(null);
  const maxRetries = 3;
  const [retryCount, setRetryCount] = useState(0);
  
  // Clean up interval on unmount
  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      console.log(`Selected file: ${selectedFile.name}, Type: ${selectedFile.type}, Size: ${selectedFile.size} bytes`);
      
      // Reset states when file changes
      setError(null);
      setProgress(0);
      setStatusMessage('');
      
      if (['txt', 'pdf', 'docx'].includes(fileExtension)) {
        setFile(selectedFile);
        console.log('File accepted for import');
      } else {
        setFile(null);
        setError('Please select a valid file (TXT or PDF)');
        console.log(`Rejected file with extension: ${fileExtension}`);
      }
    }
  };

  // Function for simulation progress during import process
  const startProgressSimulation = () => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    setProgress(0);
    
    // Create new interval with phases
    let phase = 1;
    // Phase 1: 0-30% - File upload
    // Phase 2: 30-60% - Document parsing
    // Phase 3: 60-90% - Survey creation
    
    progressIntervalRef.current = setInterval(() => {
      setProgress(prevProgress => {
        let increment;
        let newStatus = '';
        
        if (prevProgress < 30) {
          // Phase 1: Fast initial progress for file upload
          increment = 2 + Math.random() * 3;
          newStatus = 'Uploading file...';
        } else if (prevProgress < 60) {
          // Phase 2: Slower for document parsing
          increment = 0.5 + Math.random() * 1.5;
          newStatus = 'Parsing document with AI...';
        } else if (prevProgress < 90) {
          // Phase 3: Slowest for final survey creation
          increment = 0.2 + Math.random() * 0.5;
          newStatus = 'Creating survey structure...';
        } else {
          // Cap at 90%
          clearInterval(progressIntervalRef.current);
          return 90;
        }
        
        setStatusMessage(newStatus);
        
        // Calculate new progress with ceiling to prevent exceeding phase boundaries
        const newProgress = Math.min(
          prevProgress + increment, 
          phase === 1 ? 30 : phase === 2 ? 60 : 90
        );
        
        // Check if we've moved to a new phase
        if (phase === 1 && newProgress >= 30) {
          phase = 2;
        } else if (phase === 2 && newProgress >= 60) {
          phase = 3;
        }
        
        return newProgress;
      });
    }, 200);
    
    return progressIntervalRef.current;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to import');
      console.log('No file selected for import');
      return;
    }
    
    try {
      console.log(`Starting import for file: ${file.name}`);
      setLoading(true);
      setError(null);
      setRetryCount(0);
      
      // Start progress simulation
      const progressInterval = startProgressSimulation();
      
      console.log('Making API call to import survey...');
      
      try {
        const response = await importSurvey(file);
        console.log('Import API call successful:', response);
        
        clearInterval(progressInterval);
        setProgress(100);
        setStatusMessage('Import successful! Redirecting...');
        
        // Redirect to the newly created survey
        console.log(`Redirecting to survey: ${response.id}`);
        setTimeout(() => {
          navigate(`/surveys/${response.id}`);
        }, 1000);
      } catch (apiError) {
        console.error('Import API call failed with error:', apiError);
        
        // Increment retry counter if under max retries
        if (retryCount < maxRetries) {
          const currentRetry = retryCount + 1;
          setRetryCount(currentRetry);
          setStatusMessage(`Retry attempt ${currentRetry}/${maxRetries}...`);
          
          console.log(`Retrying import (${currentRetry}/${maxRetries})...`);
          
          // Try again with a slight delay
          // setTimeout(() => {
          //  handleSubmit(e);
          // }, 2000);
          
          return;
        }
        
        clearInterval(progressInterval);
        
        // Detailed error handling
        if (apiError.response) {
          console.error('Server response:', apiError.response.status, apiError.response.data);
          if (apiError.response.data && apiError.response.data.error) {
            setError(`Import failed: ${apiError.response.data.error}`);
          } else {
            setError(`Server error (${apiError.response.status}). Please try again.`);
          }
        } else if (apiError.request) {
          console.error('No response received, request detail:', apiError.request);
          setError('Network error. Server did not respond to the request. Please check your connection and try again.');
        } else {
          console.error('Error setting up request:', apiError.message);
          setError(`Import failed: ${apiError.message || 'Unknown error'}`);
        }
        
        setProgress(0);
      }
      
    } catch (err) {
      console.error('Error importing survey:', err);
      clearInterval(progressIntervalRef.current);
      setError('Failed to import survey. Please check your file and try again.');
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="mb-4">Import Survey from Document</h1>
      
      <Card className="mb-4">
        <Card.Body>
          <p>
            Upload a document file (TXT or PDF) to automatically generate a survey using AI.
            The document should contain question text, types, and possible options.
          </p>
          
          <h3 className="h5 mb-3">Supported Format Example:</h3>
          <pre className="bg-light p-3 border rounded">
{`Survey Title: Employee Satisfaction Survey
Description: Annual survey to gather employee feedback

Group: Personal Information
- What is your department? (radio) [HR, Engineering, Marketing, Sales, Other]
- How long have you worked here? (select) [0-1 years, 1-3 years, 3-5 years, 5+ years]

Group: Work Environment
- Rate your satisfaction with your work environment. (radio) [Very Satisfied, Satisfied, Neutral, Dissatisfied, Very Dissatisfied]
- What aspects of the workplace could be improved? (checkbox) [Equipment, Office Space, Meeting Rooms, Break Areas, Remote Work Options]

General Questions:
- Do you have any additional feedback? (text)
`}
          </pre>
        </Card.Body>
      </Card>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Form onSubmit={handleSubmit}>
        <Card>
          <Card.Body>
            <Form.Group controlId="formFile" className="mb-4">
              <Form.Label>Upload Document</Form.Label>
              <Form.Control 
                type="file" 
                onChange={handleFileChange}
                accept=".txt,.pdf,.docx"
                disabled={loading}
              />
              <Form.Text className="text-muted">
                Accepted formats: TXT, PDF
              </Form.Text>
            </Form.Group>
            
            {loading && (
              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span>{statusMessage || 'Processing document...'}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <ProgressBar 
                  now={progress} 
                  animated 
                  variant={
                    progress < 30 ? "info" : 
                    progress < 60 ? "primary" : 
                    progress < 90 ? "success" : 
                    "success"
                  }
                  className="mb-2"
                />
                <div className="small text-muted mt-2">
                  {progress < 30 && (
                    <>Uploading file to server. Please wait...</>
                  )}
                  {progress >= 30 && progress < 60 && (
                    <>AI is analyzing your document to extract questions and options...</>
                  )}
                  {progress >= 60 && progress < 90 && (
                    <>Creating survey structure with questions and groups...</>
                  )}
                  {progress >= 90 && (
                    <>Almost done! Finalizing your survey...</>
                  )}
                </div>
                
                {retryCount > 0 && (
                  <Alert variant="warning" className="mt-3 mb-0">
                    <small>
                      <strong>Retry attempt {retryCount}/{maxRetries}</strong> - 
                      We're having trouble with the import. Retrying automatically...
                    </small>
                  </Alert>
                )}
              </div>
            )}
            
            <div className="d-flex justify-content-between">
              <Button 
                variant="outline-secondary" 
                onClick={() => navigate('/')}
                disabled={loading && progress > 50} // Disable if we're well into processing
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary" 
                disabled={loading || !file}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {progress >= 90 ? 'Finalizing...' : 'Importing...'}
                  </>
                ) : 'Import Survey'}
              </Button>
            </div>
            
            <div className="mt-3 small text-muted">
              <p className="mb-0">
                <strong>Note:</strong> Import can take 30-60 seconds depending on document complexity.
                Please don't refresh the page during import.
              </p>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default ImportSurvey;
